import React, { useCallback, useState } from 'react';
import Immutable from 'immutable';

import { ApiService } from 'js/src/client';
import { useFormFeedback } from 'js/src/components/auth/common/useFormFeedback';
import { Toast } from 'js/src/components/common/Toast';
import Button, { BUTTON_TYPE } from 'js/src/components/common/ui/Button';
import Callout, { CALLOUT_TYPES } from 'js/src/components/common/ui/Callout';
import TextInput, {
    AUTOCOMPLETE_VALUES,
    TEXT_INPUT_VARIANTS,
    type TypeInputChangeEvent,
} from 'js/src/components/common/ui/TextInput';
import { isEmailValid } from 'js/src/libs/custom-utils';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import { isApiError } from 'js/src/types';

const ForgotPasswordForm = React.memo(function ForgotPasswordForm() {
    const [emailVal, setEmailVal] = useState('');
    const onEmailChange = useCallback(
        (e: TypeInputChangeEvent) => setEmailVal(e.currentTarget.value),
        [],
    );

    const [formFeedback, setFormFeedback] = useFormFeedback(
        Immutable.fromJS({
            success: '',
            global: '',
            email: '',
        }),
    );

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        e.stopPropagation();

        setFormFeedback({ type: 'CLEAR_ERRORS' });

        if (!isEmailValid(emailVal)) {
            setFormFeedback({
                type: 'SET_ERROR',
                payload: Immutable.fromJS({
                    email: 'Please enter a valid email.',
                }),
            });

            return false;
        }

        ApiService.passwordForgotCreate({
            body: { email: emailVal.toLowerCase() },
        })
            .then((response) => {
                setFormFeedback({
                    type: 'SET_SUCCESS',
                    payload: Immutable.fromJS(response.data),
                });
            })
            .catch((error: unknown) => {
                ErrorReporter.capture(error);
                if (isApiError(error) && error.body) {
                    setFormFeedback({
                        type: 'SET_ERROR',
                        payload: Immutable.fromJS(error.body),
                    });
                } else {
                    Toast.error('An error occurred. Please try again.');
                }
            })
            .finally(() => setEmailVal(''));
    };

    return (
        <form
            id="forgot-form"
            data-testid="forgot-form"
            method="post"
            onSubmit={handleSubmit}
            className="form-sidebar"
        >
            {formFeedback.get('success') !== '' && (
                <Callout msgType={CALLOUT_TYPES.INFO}>
                    <span>{formFeedback.get('success')}</span>
                </Callout>
            )}

            {formFeedback.get('global') !== '' && (
                <Callout msgType={CALLOUT_TYPES.ERROR}>
                    <span>{formFeedback.get('global')}</span>
                </Callout>
            )}

            <br />

            <div data-testid="email-container">
                <TextInput
                    labelText="WORK EMAIL"
                    text={emailVal}
                    variant={TEXT_INPUT_VARIANTS.STANDARD}
                    onChange={onEmailChange}
                    formFeedbackMessage={formFeedback.get('email')}
                    data-testid="email"
                    autocomplete={AUTOCOMPLETE_VALUES.EMAIL}
                    ignorePasswordManagers={false}
                />
            </div>

            <br />
            <br />

            <Button className="d-block w-100" type={BUTTON_TYPE.SUBMIT} text="Request reset link" />

            <br />
        </form>
    );
});

export default ForgotPasswordForm;
